import requestAPI from '@/logic/requestBeApi';
import singleProjectService from '@/logic/services/singleProjectService';
import store from '@/store';
import formValidators from '@/logic/helpers/forms/validators';
import getDeadlineToSend from '@/logic/helpers/dates/getDeadlineToSend';

const newProjectHelper = {
  getPreferredPlace(self: Record<string, any>): string | Record<string, string> {
    if (self.assessmentsInMyOffice) {
      return `/api/v1/addresses/${self.locationId}`;
    } else {
      return self.location;
    }
  },

  async submitProjectBasics(self: Record<string, any>, needToQuit?: boolean): Promise<void> {
    formValidators.validateForm(self, 'clientNewProject');
    self.invalidLocation = this.helpToValidatePreferredLocation(self.location);

    if (!self.formIsValid || self.invalidLocation) {
      return;
    }

    self.processingProjectRequest = true;
    const payload = {
      name: self.clientNewProject.projectName,
      position: self.clientNewProject.position,
      deadline: getDeadlineToSend(self.clientNewProject.deadline),
      prefered_place: this.getPreferredPlace(self),
      video_assessment_allowed: self.clientNewProject.videoAssessmentAllowed,
      assigment_type: 'New recruitment',
      evaluation_criteria: self.clientNewProject.assessmentType,
      reporting_format: [self.clientNewProject.format],
      competence_file: self.clientNewProject.competenceFile,
      expertises: self.getExpertisesFromUI(),
      expertise_tests: self.getExpertiseTestsFromUI(),
      price: 0,
      ...(self.isAssessorPlus && { client: '' }),
    };
    if (self.isAssessorPlus) {
      payload.client = self.getClientURIFromUI();
    }
    if (
      !self.clientNewProject.competenceFile ||
      self.clientNewProject.competenceFile.includes('http')
    ) {
      delete payload.competence_file;
    }

    let resp: Response;
    if (self.projectId) {
      payload.price = parseInt(self.price);

      resp = await requestAPI({
        method: 'PATCH',
        contentType: 'application/merge-patch+json',
        url: `/api/v1/assessment-requests/${self.projectId}`,
        payload,
      });
    } else {
      payload.price = parseInt(self.clientNewProject.pricePerAssessment);
      resp = await requestAPI({
        method: 'POST',
        url: '/api/v1/assessment-requests',
        payload,
      });
    }
    if (resp.ok) {
      const data = await resp.json();
      self.projectId = data.id;
      if (!needToQuit) {
        if (!self.userEditingProjectAfterNotFindingAssessors) {
          const path = self.isAssessorPlus ? '/assessor-new-project' : '/client-new-project';
          self.$router
            .push({
              path,
              query: { project: self.projectId, jump_to_candidates: 'true' },
            })
            .catch((err: Error) => err);
          // go to candidates section
          self.choosingCandidates = true;
        }
        self.userEditingProjectAfterNotFindingAssessors = false;
        window.scrollTo(0, 0);
      } else {
        store.commit('setRecentlyEditedProjectId', self.projectId);
        store.commit('applyAssessmentsFilter', { mode: 'projects', filter: 'requests' });
        self.$router.push({ path: '/client-tasks' });
      }
    } else if (resp.status !== 401) self.$message.error(self.$t('someErrorOccurred'));

    self.processingProjectRequest = false;
  },

  async patchProjectWithCandidates(self: Record<string, any>, needToQuit?: boolean): Promise<void> {
    self.processingProjectRequest = true;
    const payload = {
      price: parseInt(self.price),
      candidates: self.selectedCandidates.map(
        (candidate: string) => `/api/v1/candidates/${candidate}`,
      ),
      ...(self.isAssessorPlus && { client: self.getClientURIFromUI() }),
    };
    const resp = await requestAPI({
      method: 'PATCH',
      contentType: 'application/merge-patch+json',
      url: `/api/v1/assessment-requests/${self.projectId}/candidates`,
      payload,
    });

    if (resp.ok) {
      if (needToQuit) {
        store.commit('setRecentlyEditedProjectId', self.projectId);
        store.commit('applyAssessmentsFilter', { mode: 'projects', filter: 'requests' });
        if (!self.isAssessorPlus) {
          self.$router.push({ path: '/client-tasks' });
        }
      }
    } else if (resp.status !== 401) {
      self.$message.error(self.$t('someErrorOccurred'));
    }

    self.processingProjectRequest = false;
  },

  async patchProjectWithMatchingProperties(
    self: Record<string, any>,
    needToQuit?: boolean,
  ): Promise<void> {
    self.processingProjectRequest = true;
    self.specifyingMatchingProperties = false;

    const matchingProperties = (self.clientPropertiesList as {
      position: string;
      matching_property: string;
    }[])
      .filter((prop) => !!prop.matching_property)
      .map((prop) => {
        return {
          matching_property: `/api/v1/matching-properties/${prop.matching_property}`,
          position: prop.position,
        };
      });

    const payload = {
      matching_properties: self.isAssessorPlus ? [] : matchingProperties,
    };

    if (!self.prop1) delete payload.matching_properties;

    const resp = await requestAPI({
      method: 'PATCH',
      contentType: 'application/merge-patch+json',
      url: `/api/v1/assessment-requests/${self.projectId}/matching-properties`,
      payload,
    });

    if (resp.ok) {
      if (needToQuit) {
        store.commit('setRecentlyEditedProjectId', self.projectId);
        store.commit('applyAssessmentsFilter', { mode: 'projects', filter: 'requests' });
        if (!self.isAssessorPlus) {
          self.$router.push({ path: '/client-tasks' });
        } else {
          self.$message.success(self.$t('assessmentRequestCreated'));
          self.$router.push({ path: '/assessor-tasks' });
        }
      }

      self.assessmentReqCreated = true;
      self.getAssessors();
    } else if (resp.status !== 401) {
      self.$message.error(self.$t('someErrorOccurred'));
    }

    self.processingProjectRequest = false;
  },

  async patchProjectWithAssessors(self: Record<string, any>): Promise<void> {
    if (!self.selectedAssessors.length) {
      self.$message.info(self.$t('selectAtLeastOneAssessor'));
      return;
    }

    self.notifyingAssessors = true;

    const payload = {
      assessors: self.selectedAssessors.map((assessor: string) => `/api/v1/assessors/${assessor}`),
      invitation_expiration_date: self.invitationForAssessors.invitationExpiresOn,
    };

    if (!self.invitationForAssessors.invitationExpiresOn) {
      delete payload.invitation_expiration_date;
    }

    const resp = await requestAPI({
      url: `/api/v1/assessment-requests/${self.projectId}/assessors`,
      contentType: 'application/merge-patch+json',
      method: 'PATCH',
      payload,
    });

    if (resp.ok) self.assessorsNotified = true;
    else if (resp.status !== 401) self.$message.error(self.$t('someErrorOccurred'));

    self.notifyingAssessors = false;

    setTimeout(() => {
      store.commit('setRecentlyEditedProjectId', self.projectId);
      store.commit('applyAssessmentsFilter', { mode: 'projects', filter: 'requests' });
      self.$router.push({ path: '/client-tasks' });
    }, 3000);
  },

  getProject(self: Record<string, any>, projectId: string): void {
    if (!self.projectId) return;

    singleProjectService.getSingleProject({ self, projectId });
  },

  applyProjectFromStoreToUIModel(self: Record<string, any>, proj: Record<string, any>): void {
    // apply candidates
    if (proj.candidates_list && proj.candidates_list.length > 0) {
      self.selectedCandidates.push(
        ...(proj.candidates_list ?? []).map((cand: { id: string }) => cand.id),
      );
    }
    // apply expertise test
    if (proj.expertise_tests.length > 0) {
      self.expertiseTests.push(...proj.expertise_tests.map((test: { id: string }) => test.id));
    }
    // apply basics
    self.clientNewProject.projectName = proj.name;
    self.clientNewProject.position = proj.position;
    self.clientNewProject.deadline = proj.deadline;
    if (self.isAssessorPlus) {
      self.clientNewProject.pricePerAssessment = null;
    } else {
      self.clientNewProject.pricePerAssessment = proj.price / (proj.candidates_list.length || 1);
    }
    self.clientNewProject.price = proj.price;
    self.clientNewProject.format = proj.reporting_format[0];

    self.clientNewProject.department = self.departments.find(
      (obj) => obj.name.toLowerCase() === proj.function.toLowerCase(),
    ).id;
    self.clientNewProject.level = self.levels.find(
      (obj) => obj.name.toLowerCase() === proj.expertises.levels.toLowerCase(),
    ).id;
    self.clientNewProject.industry = self.industries.find(
      (obj) => obj.name.toLowerCase() === proj.expertises.industries.toLowerCase(),
    ).id;
    self.clientNewProject.tool = self.tools.find((obj) =>
      obj.name.toLowerCase().includes(proj.expertises?.tools.toLowerCase()),
    ).id;
    self.clientNewProject.language = self.languages.find(
      (lang) => lang.slug.split('-')[1] === proj.expertises.languages.toLowerCase(),
    ).id;
    self.clientNewProject.assessmentType = self.assessmentTypes.find(
      (obj) => obj.name.toLowerCase() === proj.expertises['assessment-types'].toLowerCase(),
    ).id;

    const preferredPlace = proj.prefered_place;

    if (preferredPlace) {
      self.clientNewProject.city = preferredPlace.city;
      self.clientNewProject.country = preferredPlace.country;
      self.clientNewProject.street = preferredPlace.street;
      self.clientNewProject.houseNumber = preferredPlace.house_number;
      self.clientNewProject.apartmentNumber = preferredPlace.apartment_number;
      self.clientNewProject.postalCode = preferredPlace.postal_code;
    }

    self.clientNewProject.competenceFile = proj.competence_file;
    self.clientNewProject.videoAssessmentAllowed = proj.video_assessment_allowed;

    // apply matching criteria
    if (self.isAssessorPlus) {
      self.clientPropertiesList.forEach((prop) => {
        prop.position = 0;
        prop.matching_property = '';
      });
    } else {
      proj.matching_properties.forEach(
        (prop: { position: number; matching_property: { id: string } }) => {
          self.clientPropertiesList[prop.position - 1].position = prop.position;
          self.clientPropertiesList[prop.position - 1].matching_property =
            prop.matching_property.id;
        },
      );
    }
  },

  async getMinimumPricePerAssessment(self: {
    $message: { error: (arg0: any) => void };
    $t: (arg0: string) => string;
  }): Promise<void> {
    const resp = await requestAPI({
      url: '/api/v1/assessors/minimum-assessment-price',
      method: 'GET',
    });

    if (resp.ok) {
      const data = await resp.json();
      store.commit('meta/setMinimalPricePerAssessment', data.minimum_assessment_price);
    } else if (resp.status !== 401) self.$message.error(self.$t('someErrorOccurred'));
  },

  helpToValidatePreferredLocation(location: {
    city: string;
    country: string;
    apartment_number: string;
    street: string;
    house_number: string;
    postal_code: string;
  }): boolean {
    if (Object.values(location).every((value) => value === '')) return false;
    if (Object.values(location).filter((value) => value !== '').length === 1) return true;
    if (!location.city && !location.country) return true;

    return !!(
      (location.city && !location.country) ||
      (!location.city && location.country) ||
      (location.apartment_number && !location.house_number) ||
      (location.street && !location.house_number) ||
      (!location.street && location.house_number)
    );
  },
};

export default newProjectHelper;
